import { StandardObject } from "utils";
import {
  handleResp,
  getURLWithQueryParams,
  getDefaultHeaders,
  extendFetchOptions,
} from "./utils";
import type {
  CommonResponse,
  GenericResponse,
  Sortable,
  WrappedCashifyFunction,
  WrappedCashifyPageableQuery,
} from "./utils";

export interface ProductService {
  listProduct: ReturnType<typeof listProduct>;
  getProductDetail: ReturnType<typeof getProductDetail>;
  putProductDetail: ReturnType<typeof putProductDetail>;
  postProduct: ReturnType<typeof postProduct>;
  deleteProductDetail: ReturnType<typeof deleteProductDetail>;
}

interface ProductSearchCriteria extends Sortable {
  id?: number;
  productCode?: string;
  productName?: string;
  "createTime.from"?: string;
  "createTime.to"?: string;
  "lastUpdateTime.from"?: string;
  "lastUpdateTime.to"?: string;
}

// InterestRateType: 1: MONTHLY_FLAT_RATE, 2: ANNUAL_INTEREST_RATE
type InterestRateType = "1" | "2";
export interface ProductDTO {
  productCode: string;
  productVariant?: string;
  productName?: string;
  productDescription?: string;
  interestRate: number;
  interestRateType: InterestRateType;
  processingFeeRate: number;
  minimumProcessingFee: number;
  absoluteProcessingFee: number;
  cashRebate: number;
  effectiveStart?: string;
  effectiveEnd?: string;
  additionalInfo?: StandardObject;
  createTime?: string;
  lastUpdateTime?: string;
  lastUpdateAdmin?: string;
  productId: number;
}

export interface UpdateProductRequestBody {
  productName?: string;
  productDescription?: string;
  interestRate?: number;
  interestRateType?: string;
  processingFeeRate?: number;
  minimumProcessingFee?: number;
  absoluteProcessingFee?: number;
  cashRebate?: number;
  effectiveStart?: string;
  effectiveEnd?: string;
  additionalInfo?: StandardObject;
}

export interface CreateProductRequestBody {
  productCode: string;
  productVariant?: string;
  productName?: string;
  productDescription?: string;
  interestRate: number;
  interestRateType: string;
  processingFeeRate: number;
  minimumProcessingFee: number;
  absoluteProcessingFee: number;
  cashRebate: number;
  effectiveStart?: string;
  effectiveEnd?: string;
  additionalInfo?: StandardObject;
}

export interface UpdateProductArgs {
  code: string;
  productVariant: string;
  detail: UpdateProductRequestBody;
}

export const listProduct: WrappedCashifyPageableQuery<
  ProductSearchCriteria,
  ProductDTO
> = ({ urlPrefix, options }) => {
  return async (queryParams) => {
    let url = getURLWithQueryParams(
      `${urlPrefix}/api/v1/fuboncredit/loan/product`,
      queryParams
    );
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const getProductDetail: WrappedCashifyFunction<
  { code: string },
  GenericResponse<ProductDTO[]>
> = ({ urlPrefix, options }) => {
  return async ({ code }) => {
    let url = `${urlPrefix}/api/v1/fuboncredit/loan/product/${code}`;
    let opts = extendFetchOptions(
      {
        method: "GET",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const putProductDetail: WrappedCashifyFunction<
  UpdateProductArgs,
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ code, productVariant, detail }) => {
    let url = getURLWithQueryParams(
      `${urlPrefix}/api/v1/fuboncredit/loan/product/${code}`,
      { productVariant }
    );
    let opts = extendFetchOptions(
      {
        method: "PUT",
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(detail),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const postProduct: WrappedCashifyFunction<
  CreateProductRequestBody,
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async (details) => {
    let url = `${urlPrefix}/api/v1/fuboncredit/loan/product`;
    let opts = extendFetchOptions(
      {
        method: "POST",
        body: JSON.stringify(details),
        headers: {
          ...getDefaultHeaders(),
          "Content-Type": "application/json",
        },
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};

export const deleteProductDetail: WrappedCashifyFunction<
  { code: string; productVariant?: string },
  CommonResponse
> = ({ urlPrefix, options }) => {
  return async ({ code, productVariant }) => {
    let url: string = "";
    if (typeof productVariant === undefined) {
      url = `${urlPrefix}/api/v1/fuboncredit/loan/product/${code}`;
    } else {
      url = getURLWithQueryParams(
        `${urlPrefix}/api/v1/fuboncredit/loan/product/${code}`,
        { productVariant }
      );
    }

    let opts = extendFetchOptions(
      {
        method: "DELETE",
        headers: getDefaultHeaders(),
      },
      options
    );
    return fetch(url, opts).then((resp) => handleResp(resp));
  };
};
